import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { Tabs, TabItem } from "./Tabs"
import ErrorBoundary from "./ErrorBoundary"
import validator from '@rjsf/validator-ajv8';
import Form from '@rjsf/core';
import Editor, { loader } from "@monaco-editor/react";

const ParametersForm = ({
  schema,
  parameters,
  fieldSelector,
  allowRawInput = false,
  isSearchtermConf = false
}) => {
  const [params, setParams] = useState(parameters)
  const [errors, setErrors] = useState([])
  const editorRef = useRef(null)

  useEffect(() => {
    const json = JSON.stringify(schema);
    const blob = new Blob([json], { type: "application/json" });
    const uri = URL.createObjectURL(blob);

    loader
      .init()
      .then((m) => {
        m.languages.json.jsonDefaults.setDiagnosticsOptions({
          validate: true,
          enableSchemaRequest: true,
          schemas: [{
            uri,
            fileMatch: ['*'],
            schema
          }]
        });
      })
      .catch(error =>
        console.error("An error occurred during initialization of Monaco: ", error)
      );
  }, []);

  const uiSchema = {
    'ui:submitButtonOptions': {
      norender: true,
    },
  };

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    const model = editor.getModel();

    editor.onDidChangeModelDecorations(() => {
      if (model === null || model.getLanguageId() !== "json")
        return;

      const owner = model.getLanguageId();
      const markers = monaco.editor.getModelMarkers({ owner });
      const errs = markers || [];
      setErrors(errs);
    });
  };

  function ErrorListTemplate(props) {
    const { errors } = props
    if (errors.length > 0) {
      return (
        <div id="params_has_errors" className="p-4 bg-red-200">
          <h3 className="text-lg font-medium leading-6 text-red-800">Errors were found:</h3>
          <ul className="mt-2">
            {errors.map(error => (
              <li key={error.stack || error.message}>
                {error.stack || error.message}
              </li>
            ))}
          </ul>
        </div>
      )
    }

    return (
      <div className="p-4 bg-green-200">
        <h3 className="text-lg font-medium leading-6 text-green-800">Parameters are valid!</h3>
      </div>
    )
  }

  const handleFormChange = (change) => {
    setErrors(change.errors)
    handleDataChange(change.formData)
  }

  const handleDataChange = (data) => {
    setParams(data)
    const el = document.querySelector(fieldSelector)
    el.value = JSON.stringify(data)
  }

  const handleSubmit = () => {
    document.querySelector(".form-submit").click()
  }

  function addClassNameListener(elem,) {
    var checkSubmit = window.setInterval(function () {
      try {
        var className = elem.className;
        var secondaryButton = document.querySelector("button[type=submit]")
        var errorsMessage = document.getElementById("params_has_errors")

        if (!className.includes("cursor-not-allowed") && errorsMessage == null) {
          secondaryButton.classList.remove("opacity-50", "cursor-not-allowed");
        } else {
          secondaryButton.classList.add("opacity-50", "cursor-not-allowed");
        }
      } catch (error) {
        console.warn(error);
        window.clearInterval(checkSubmit);
      }
    }, 10);
  }

  const targetEl = document.querySelector(".form-submit");
  addClassNameListener(targetEl);

  const schemaExists = Object.keys(schema).length > 0;

  if (!allowRawInput && !schemaExists) {
    return (
      <div className="p-2 bg-white flex justify-center">
        <p>No parameters section to display.</p>
      </div>
    );
  } else {
    return (
      <ErrorBoundary>
        <div className="bg-white">
          <ErrorListTemplate errors={errors} />
          <Tabs defaultIndex={schemaExists ? "0" : "1"}>
            {schemaExists &&
              <TabItem label="UI Form" index="0">
                <div className="p-5">
                  {isSearchtermConf ?
                    <div>
                      <p>UI Form is disabled for search related measure_configs due to possible data size!</p>
                      <p>Please use the Raw JSON editor.</p>
                      <br />
                      <p>(if not already shown, you can enable it by checking 'User Editable Parameters' above.)</p>
                    </div> :
                    <Form
                      schema={schema}
                      formData={params}
                      validator={validator}
                      onChange={handleFormChange}
                      uiSchema={uiSchema}
                      showErrorList={false}
                      liveValidate
                    />
                  }
                </div>
              </TabItem>
            }
            {allowRawInput &&
              <TabItem label="Raw JSON" index="1">
                <Editor
                  language="json"
                  onMount={handleEditorDidMount}
                  value={JSON.stringify(params, null, 2)}
                  onChange={(val) => handleDataChange(JSON.parse(val))}
                  height="15em"
                />
              </TabItem>
            }
          </Tabs>
          <div className="form-submit">
            <button className="save-param-btn" onClick={handleSubmit} type="submit">Save Parameters</button>
          </div>
        </div>
      </ErrorBoundary>
    );
  }
}


ParametersForm.propTypes = {
  schema: PropTypes.object,
  parameters: PropTypes.object,
  fieldSelector: PropTypes.string,
  allowRawInput: PropTypes.bool
};

export default ParametersForm;
